import React, { useRef, useEffect, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getProjectGeoJSON, getProjectCoordinates } from '../../../utils/mapDataMapping';
import { getDeviceLayersForProject, createDeviceLeafletIcon } from '../../../utils/mapLayerUtils';
import { getMapStyleUrl, getMapStyleOptions } from '../../../utils/mapStyles';

interface ProjectOverviewMapProps {
  projectName?: string;
}

const escapeHtml = (value: any) =>
  String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] || char));

const ProjectOverviewMap: React.FC<ProjectOverviewMapProps> = ({ projectName = 'Mangrove' }) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const DEVICE_LAYERS = useMemo(() => getDeviceLayersForProject(projectName), [projectName]);
  const mapInitialized = useRef<boolean>(false);

  useEffect(() => {
    if (!mapContainer.current) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const center = getProjectCoordinates(projectName);
    const geoData = getProjectGeoJSON(projectName);

    const leafletCenter: [number, number] = Array.isArray(center) && center.length >= 2
      ? [Number(center[1]), Number(center[0])]
      : [0, 0];

    try {
      mapRef.current = L.map(mapContainer.current as HTMLDivElement, {
        center: leafletCenter,
        zoom: 12,
        zoomControl: true,
      });

      L.tileLayer(getMapStyleUrl('normal'), getMapStyleOptions('normal')).addTo(mapRef.current);

      // Add project GeoJSON (boundary fill + outline)
      if (geoData) {
        L.geoJSON(geoData as any, {
          style: () => ({
            color: '#16a34a',
            weight: 2,
            fillColor: '#22c55e',
            fillOpacity: 0.35,
          }),
          onEachFeature: (feature: any, layer: any) => {
            if (feature.properties && feature.properties.name) {
              layer.bindPopup(String(feature.properties.name));
            }
          },
        }).addTo(mapRef.current);

        // Add device points as circle markers (if present in geoData features)
        try {
          const features = (geoData as any).features || [];
          features.forEach((f: any) => {
            if (!f || !f.geometry) return;
            if (f.geometry.type === 'Point') {
              const coords = f.geometry.coordinates; // [lng, lat]
              const latlng: [number, number] = [coords[1], coords[0]];
              const type = f.properties?.Type || f.properties?.type || '';
              const layerDef = DEVICE_LAYERS.find((d: any) => d.type === type);
              const color = layerDef?.color || '#ff5722';
              const marker = L.marker(latlng, {
                icon: createDeviceLeafletIcon(type, color),
              }).addTo(mapRef.current as L.Map);
              if (f.properties) {
                const popupText = Object.keys(f.properties).map(k => `${k}: ${f.properties[k]}`).join('<br/>');
                marker.bindPopup(popupText);
              }
            }
          });
        } catch (e) {
          console.warn('Failed to add device markers', e);
        }
      }
    } catch (err) {
      console.error('Leaflet map init failed', err);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [projectName]);

  return (
    <div style={{ width: '100%', marginBottom: '16px' }}>
      {/* Map */}
      <div style={{ height: '440px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      </div>
    </div>
  );
};

export default ProjectOverviewMap;
